import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Container, Navbar, Nav, Button, Row, Col, NavDropdown } from 'react-bootstrap';
import BarChartIcon from '@mui/icons-material/BarChart';
import HomeIcon from '@mui/icons-material/Home';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DescriptionIcon from '@mui/icons-material/Description';
import CodeIcon from '@mui/icons-material/Code';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import UploadIcon from '@mui/icons-material/Upload';
import ErrorIcon from '@mui/icons-material/Error';
import EmailIcon from '@mui/icons-material/Email';
import SearchIcon from '@mui/icons-material/Search';
import { Autocomplete } from '@mui/material';
import TextField from '@mui/material/TextField';
import { useSearchScriptMutation } from '../Redux/Script';
import { ActiveRoute, SidebarMenu } from '../Menu';
import { useRole } from '../customHook/getrole';
import { readSessionTokens } from '../utils/localStorage';
import { refreshSessionFlags } from '../utils/sessionFlags';
import { useSignOutMutation } from '../Redux/AuthSlice';
import AutoComplete, { inputStyles } from '../Comopnent/AutoComplete';

const AuthLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const admin = useRole();
  const [signout] = useSignOutMutation();
  const [searchScript, { isLoading, data }]: any = useSearchScriptMutation();
  const [searchText, setSearchText] = useState('');

  // Fetch the account's staff flag each time the signed in app opens. A
  // session that logged in before sprint 17 has no flag stored, and this is
  // what lets it see Edit and Delete without signing out and in again.
  useEffect(() => {
    refreshSessionFlags();
  }, []);

  const handleSearch = (event: React.SyntheticEvent, option: any) => {
    let url;
    switch (option?.subject) {
      case 'summaries':
        url = ActiveRoute.TapeSummaryResult;
        break;
      case 'reports':
        url = ActiveRoute.ReportDetails;
        break;
      case 'scripts':
        url = ActiveRoute.ScriptDetails;
    }

    if (url) {
      setSearchText(option.name);
      navigate(`/app/${url.url}/${option.id}`);
    }
  };

  const fetchSearchResults = (text: string) => {
    if (!text || !text.trim()) return;
    const encodedQuery = encodeURIComponent(text);
    searchScript({ value: encodedQuery });
  };

  useEffect(() => {
    fetchSearchResults(searchText);
  }, [searchText]);

  const searchResults = useMemo(() => {
    if (typeof data === 'object') {
      const res = Object.keys(data)
        .filter((subject) => subject != 'categories')
        .map((subject) =>
          data[subject].map((item: object) => ({ ...item, subject })),
        )
        .flat();
      return res;
    }
  }, [data]);

  const logout = async () => {
    // The login as stored now, so the server revokes this session's refresh
    // token. Signed out locally whether or not that request succeeds.
    const tokens = readSessionTokens();
    if (tokens) {
      await signout({ token: tokens });
    }
    localStorage.removeItem('login');
    navigate('/');
  };

  return (
    <div className="auth-layout">
      <Navbar bg="dark" variant="dark" expand="lg" className="shadow-sm">
        <Container fluid>
          <Navbar.Brand as={Link} to="/app/home" className="d-flex align-items-center">
            <BarChartIcon style={{ fontSize: 24 }} className="me-2" />
            <span className="fw-bold">Total Returns</span>
          </Navbar.Brand>

          <Navbar.Toggle aria-controls="auth-navbar" />

          <Navbar.Collapse id="auth-navbar">
            <Nav className="me-auto">
              <Nav.Link
                as={Link}
                to="/app/dashboard"
                active={location.pathname === '/app/dashboard'}
              >
                <DashboardIcon style={{ fontSize: 18 }} className="me-1" />
                Dashboard
              </Nav.Link>
              <Nav.Link
                as={Link}
                to="/app/dashboard/etf-categories"
                active={location.pathname.startsWith('/app/dashboard/etf')}
              >
                <TrendingUpIcon style={{ fontSize: 18 }} className="me-1" />
                Total Returns Dashboard
              </Nav.Link>
              <Nav.Link
                as={Link}
                to="/app/allscripts"
                active={location.pathname.startsWith('/app/allscripts')}
              >
                <CodeIcon style={{ fontSize: 18 }} className="me-1" />
                Scripts
              </Nav.Link>
              <Nav.Link
                as={Link}
                to="/app/Report"
                active={location.pathname.startsWith('/app/Report')}
              >
                <DescriptionIcon style={{ fontSize: 18 }} className="me-1" />
                Reports
              </Nav.Link>
              <Nav.Link
                as={Link}
                to="/app/model-summary"
                active={location.pathname.startsWith('/app/model-summary')}
              >
                <SummarizeIcon style={{ fontSize: 18 }} className="me-1" />
                Model Summary
              </Nav.Link>
              <Nav.Link
                as={Link}
                to="/app/scriptTree"
                active={location.pathname.startsWith('/app/scriptTree')}
              >
                <AccountTreeIcon style={{ fontSize: 18 }} className="me-1" />
                Script Tree
              </Nav.Link>
              {admin === 'Admin' && (
                <Nav.Link
                  as={Link}
                  to="/app/upload"
                  active={location.pathname.startsWith('/app/upload')}
                >
                  <UploadIcon style={{ fontSize: 18 }} className="me-1" />
                  Upload
                </Nav.Link>
              )}
              {admin === 'Admin' && (
                <Nav.Link
                  as={Link}
                  to="/app/error-handling"
                  active={location.pathname.startsWith('/app/error-handling')}
                >
                  <ErrorIcon style={{ fontSize: 18 }} className="me-1" />
                  Errors
                </Nav.Link>
              )}
            </Nav>

            {/* Search Bar */}
            <div className="d-flex align-items-center me-3" style={{ minWidth: '250px' }}>
              <Autocomplete
                disablePortal
                options={searchResults || []}
                getOptionLabel={(option: any) =>
                  option ? `${option.name || ''}  (${option.subject})` : ''
                }
                renderInput={(params) => (
                  <TextField
                    value={searchText}
                    onChange={(ev) => setSearchText(ev.target.value)}
                    {...params}
                    label="Search"
                    size="small"
                  />
                )}
                onChange={handleSearch}
                sx={{ background: 'white', ...inputStyles, borderRadius: 1, width: '100%' }}
                fullWidth
                size="small"
                noOptionsText="type name of a script, report, or model"
              />
            </div>

            {/* User Menu */}
            <Nav>
              <NavDropdown
                title={
                  <span className="text-light">
                    <PersonIcon style={{ fontSize: 20 }} className="me-1" />
                    Account
                  </span>
                }
                id="user-dropdown"
                align="end"
              >
                <NavDropdown.Item as={Link} to={`/app/${ActiveRoute.UserProfile.path}`}>
                  <PersonIcon style={{ fontSize: 18 }} className="me-1" />
                  Profile
                </NavDropdown.Item>
                <NavDropdown.Item
                  onClick={() => {
                    window.location.href = `${process.env.REACT_APP_ADMIN_URL}`;
                  }}
                >
                  Admin Portal
                </NavDropdown.Item>
                <NavDropdown.Divider />
                <NavDropdown.Item onClick={logout}>
                  <LogoutIcon style={{ fontSize: 18 }} className="me-1" />
                  Sign Out
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <div className="auth-content">
        <Suspense
          fallback={
            <div className="loader-center">
              <div className="spinner-border text-dark" role="status"></div>
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </div>

      <footer className="bg-dark text-light py-4 mt-5">
        <Container>
          <Row className="g-4">
            {/* About Section */}
            <Col md={4}>
              <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                About Total Returns
              </h6>
              <p style={{ fontSize: '13px', color: '#9ca3af', marginBottom: 0 }}>
                Institutional-grade investment research and market analytics platform,
                accessible to anyone who wants to learn.
              </p>
            </Col>

            {/* Contact Section */}
            <Col md={4}>
              <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                Contact Us
              </h6>
              <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                <div className="d-flex align-items-center mb-2">
                  <EmailIcon style={{ fontSize: 16, marginRight: '8px' }} />
                  <a
                    href="mailto:support@totalreturns.net"
                    style={{ color: '#9ca3af', textDecoration: 'none' }}
                    onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                    onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                  >
                    support@totalreturns.net
                  </a>
                </div>
                <p style={{ marginBottom: 0 }}>
                  For inquiries, feedback, or support requests
                </p>
              </div>
            </Col>

            {/* Legal Section */}
            <Col md={4}>
              <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '14px' }}>
                Legal
              </h6>
              <div style={{ fontSize: '13px' }}>
                <Link
                  to="/terms"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginBottom: '8px' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Disclaimer &amp; Terms of Use
                </Link>
                <Link
                  to="/privacy"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginBottom: '8px' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/about"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block', marginBottom: '8px' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  style={{ color: '#9ca3af', textDecoration: 'none', display: 'block' }}
                  onMouseOver={(e) => e.currentTarget.style.color = '#3b82f6'}
                  onMouseOut={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  Contact Us
                </Link>
              </div>
            </Col>
          </Row>

          {/* Bottom Bar */}
          <div
            className="text-center pt-4 mt-4"
            style={{ borderTop: '1px solid #374151' }}
          >
            <small style={{ color: '#9ca3af', fontSize: '12px' }}>
              &copy; {new Date().getFullYear()} Total Returns. All rights reserved.
              Data provided for informational purposes only and should not be considered investment advice.
            </small>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default AuthLayout;
